import React, { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { Download, Loader, Play } from "react-feather";

import { getFileSize } from "../../../../utils/fileAttaches";
import { getInternalAttachmentUrl, getInternalGroupAttachmentUrl } from "../../../../utils/internalAttachments";

import { FileFormatIcon } from "./InternalFilePreview";
import { downloadAttachment } from "./downloadAttachment";

import "./internal-attachments.scss";

const DownloadButton = ({ url, fileName }) => {
    const [loading, setLoading] = useState(false);

    const handleDownload = useCallback(async (e) => {
        /** O clique no botão não pode também abrir o visualizador da bolha. */
        e.stopPropagation();
        setLoading(true);
        try {
            await downloadAttachment(url, fileName);
        } catch (error) {
            console.error('Erro ao baixar o anexo:', error);
        } finally {
            setLoading(false);
        }
    }, [url, fileName]);

    return (
        <span role="button" className="internal-message-attachment-download" onClick={handleDownload}>
            {loading ? <Loader size={16} className="internal-attachment-spin" /> : <Download size={16} />}
        </span>
    );
}

/** Renderiza os anexos dentro da bolha de mensagem do chat interno.
 *  Não reaproveita o `MessageAttachments`/`Thumbnail` das mensagens com clientes porque
 *  aqueles mostram só uma miniatura — aqui o usuário precisa ver nome, tamanho e ter
 *  como baixar, além de abrir o visualizador.
 */
const InternalMessageAttachments = ({ messageId, attachments, onOpenViewer, isGroup = false }) => {
    const intl = useIntl();

    if (!attachments || attachments.length === 0) return null;

    return (
        <div className="internal-message-attachments-ui">
            {attachments.map((attachment, index) => {
                const url = isGroup ? getInternalGroupAttachmentUrl(attachment) : getInternalAttachmentUrl(attachment, messageId);
                const contentType = attachment.contentType || '';
                const size = getFileSize(attachment.sizeBytes || 0);

                /** Sem `storageName` o anexo ainda não foi persistido: é o registro local do
                 *  que acabou de ser enviado. Mostra o que foi enviado, mas não é clicável. */
                if (!url) {
                    return (
                        <div key={`internal-attachment-${index}`} className="internal-message-attachment file pending">
                            <FileFormatIcon fileName={attachment.fileName} className="internal-message-attachment-icon" />
                            <div className="internal-message-attachment-info">
                                <span className="internal-message-attachment-name">{attachment.fileName}</span>
                                <span className="internal-message-attachment-size">
                                    {intl.formatMessage({ id: "Enviando..." })} · {size}
                                </span>
                            </div>
                        </div>
                    );
                }

                if (contentType.startsWith('audio/')) {
                    return (
                        <div key={`internal-attachment-${index}`} className="internal-message-attachment audio">
                            <audio src={url} controls controlsList="nodownload" />
                            <span className="internal-message-attachment-size">{size}</span>
                        </div>
                    );
                }

                if (contentType.startsWith('image/')) {
                    return (
                        <div
                            key={`internal-attachment-${index}`}
                            className="internal-message-attachment image"
                            onClick={() => onOpenViewer(index)}
                        >
                            <img src={url} alt={attachment.fileName} />
                            <span className="internal-message-attachment-badge">{size}</span>
                        </div>
                    );
                }

                if (contentType.startsWith('video/')) {
                    return (
                        <div
                            key={`internal-attachment-${index}`}
                            className="internal-message-attachment video"
                            onClick={() => onOpenViewer(index)}
                        >
                            <video src={url} muted preload="metadata" />
                            <span className="internal-message-attachment-play"><Play size={20} /></span>
                            <span className="internal-message-attachment-badge">{size}</span>
                        </div>
                    );
                }

                return (
                    <div
                        key={`internal-attachment-${index}`}
                        className="internal-message-attachment file"
                        onClick={() => onOpenViewer(index)}
                    >
                        <FileFormatIcon fileName={attachment.fileName} className="internal-message-attachment-icon" />
                        <div className="internal-message-attachment-info">
                            <span className="internal-message-attachment-name">{attachment.fileName}</span>
                            <span className="internal-message-attachment-size">{size}</span>
                        </div>
                        <DownloadButton url={url} fileName={attachment.fileName} />
                    </div>
                );
            })}
        </div>
    );
}

export default InternalMessageAttachments;
